import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { dropDownSelectionStyles } from '../../common/timeLiveFunction';
import fetchAPI from '../../common/fetchAPI';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import moment from 'moment';
import { getGlobalAccountPreferenceDataAsync } from '../../common/GlobalSlice';
import { getTimer } from '../timesheet/timesheetSlice';

declare const chrome: any;

interface ExtensionTimerProps {
  onSwitchToManual: () => void;
}

export const ExtensionTimer: React.FC<ExtensionTimerProps> = ({ onSwitchToManual }) => {
  const dispatch = useAppDispatch();


  const { 
    sessionAccountEmployeeId, 
    timeZoneId, 
    showClientInTimesheet, 
    showCostCenterInTimeSheet, 
    showWorkTypeInTimeSheet 
  } = useAppSelector((state) => state.globalState);
  
  const { defaultWorkTypeId } = useAppSelector((state) => state.timesheet);


  const showClientFlag: boolean = Boolean(showClientInTimesheet);
  const showCostCenterFlag: boolean = Boolean(showCostCenterInTimeSheet);
  const showWorkTypeFlag: boolean = Boolean(showWorkTypeInTimeSheet);


  const [activeEmpId, setActiveEmpId] = useState<number | string | null>(null);
  const [isClientVisible, setIsClientVisible] = useState<boolean>(showClientFlag);
  const [isCostCenterVisible, setIsCostCenterVisible] = useState<boolean>(showCostCenterFlag);
  const [isWorkTypeVisible, setIsWorkTypeVisible] = useState<boolean>(showWorkTypeFlag);
  const [isStorageLoaded, setIsStorageLoaded] = useState<boolean>(false);

  const [clientOptions, setClientOptions] = useState<{ value: any; label: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ value: any; label: string }[]>([]);
  const [taskOptions, setTaskOptions] = useState<{ value: any; label: string }[]>([]);
  const [costCenterOptions, setCostCenterOptions] = useState<{ value: any; label: string }[]>([]);
  const [workTypeOptions, setWorkTypeOptions] = useState<{ value: any; label: string }[]>([]);

  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState<any>(null);
  const [selectedWorkType, setSelectedWorkType] = useState<any>(null);


  const [savedIds, setSavedIds] = useState<{
    clientId?: any;
    projectId?: any;
    taskId?: any;
    costCenterId?: any;
    workTypeId?: any;
  }>({});


  const [activeTimerMeta, setActiveTimerMeta] = useState<{
    clientId?: any;
    projectId?: any;
    taskId?: any;
    costCenterId?: any;
    workTypeId?: any;
  }>({});

  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingCostCenters, setLoadingCostCenters] = useState(false);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);

  const [timerState, setTimerState] = useState<'idle' | 'running' | 'paused' | 'stopped'>('idle');
  const [seconds, setSeconds] = useState(0);
  const [description, setDescription] = useState('');
  const [lastEntryTimerId, setLastEntryTimerId] = useState<any>(null);
  const [accountEmployeeTimeEntryPeriodId, setAccountEmployeeTimeEntryPeriodId] = useState<any>(null);
  const [periodDates, setPeriodDates] = useState({ startDate: '', endDate: '', viewType: 'Weekly' });


  const persistStorage = (dataToSave: Record<string, any>) => {
    if (typeof chrome !== 'undefined' && chrome.storage?.local) {
      chrome.storage.local.set(dataToSave);
    } else {
      Object.entries(dataToSave).forEach(([k, v]) => {
        if (v === null || v === undefined) localStorage.removeItem(k);
        else localStorage.setItem(k, JSON.stringify(v));
      });
    }
  };


  useEffect(() => {
    setIsClientVisible(showClientFlag);
    setIsCostCenterVisible(showCostCenterFlag);
    setIsWorkTypeVisible(showWorkTypeFlag);
  }, [showClientFlag, showCostCenterFlag, showWorkTypeFlag]);


  useEffect(() => {
    const keysToFetch = [
      'sessionAccountEmployeeId',
      'accountEmployeeId',
      'showClientInTimesheet',
      'showCostCenterInTimeSheet',
      'showWorkTypeInTimeSheet',
      'timeZoneId',
      'manual_clientId',
      'manual_projectId',
      'manual_taskId',
      'manual_costCenterId',
      'manual_workTypeId'
    ];

    if (typeof chrome !== 'undefined' && chrome.storage?.local) {
      chrome.storage.local.get(keysToFetch, (result: any) => {
        const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId || sessionAccountEmployeeId;
        if (resolvedEmpId) setActiveEmpId(Number(resolvedEmpId));

        if (result?.showClientInTimesheet !== undefined) setIsClientVisible(Boolean(result.showClientInTimesheet));
        if (result?.showCostCenterInTimeSheet !== undefined) setIsCostCenterVisible(Boolean(result.showCostCenterInTimeSheet));
        if (result?.showWorkTypeInTimeSheet !== undefined) setIsWorkTypeVisible(Boolean(result.showWorkTypeInTimeSheet));

        setSavedIds({
          clientId: result?.manual_clientId ?? null,
          projectId: result?.manual_projectId ?? null,
          taskId: result?.manual_taskId ?? null,
          costCenterId: result?.manual_costCenterId ?? null,
          workTypeId: result?.manual_workTypeId ?? null,
        });

        setIsStorageLoaded(true);
        dispatch(getGlobalAccountPreferenceDataAsync() as any);
      });
    } else {
      if (sessionAccountEmployeeId) setActiveEmpId(Number(sessionAccountEmployeeId));
      const parseLocal = (k: string) => {
        const val = localStorage.getItem(k);
        return val ? JSON.parse(val) : null;
      };
      setSavedIds({
        clientId: parseLocal('manual_clientId'),
        projectId: parseLocal('manual_projectId'),
        taskId: parseLocal('manual_taskId'),
        costCenterId: parseLocal('manual_costCenterId'),
        workTypeId: parseLocal('manual_workTypeId'),
      });
      setIsStorageLoaded(true);
      dispatch(getGlobalAccountPreferenceDataAsync() as any);
    }
  }, [sessionAccountEmployeeId, dispatch]);

  const handleClearAll = () => {
    setSelectedClient(null);
    setSelectedProject(null);
    setSelectedTask(null);
    setSelectedCostCenter(null);
    setSelectedWorkType(null);
    setDescription('');
    setSavedIds({});

    persistStorage({
      manual_clientId: null,
      manual_projectId: null,
      manual_taskId: null,
      manual_costCenterId: null,
      manual_workTypeId: null,
    });
  };

  const handleClientChange = (opt: any) => {
    setSelectedClient(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, clientId: val, projectId: null, taskId: null, costCenterId: null }));
    
    persistStorage({
      manual_clientId: val,
      manual_projectId: null,
      manual_taskId: null,
      manual_costCenterId: null,
    });

    if (!opt) {
      setSelectedProject(null);
      setSelectedTask(null);
      setSelectedCostCenter(null);
    }
  };

  const handleProjectChange = (opt: any) => {
    setSelectedProject(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, projectId: val, taskId: null, costCenterId: null }));
    
    persistStorage({
      manual_projectId: val,
      manual_taskId: null,
      manual_costCenterId: null,
    });

    if (!opt) {
      setSelectedTask(null);
      setSelectedCostCenter(null);
    }
  };

  const handleTaskChange = (opt: any) => {
    setSelectedTask(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, taskId: val, costCenterId: null }));
    
    persistStorage({
      manual_taskId: val,
      manual_costCenterId: null,
    });

    if (!opt) {
      setSelectedCostCenter(null);
    }
  };

  const handleCostCenterChange = (opt: any) => {
    setSelectedCostCenter(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, costCenterId: val }));
    persistStorage({ manual_costCenterId: val });
  };

  const handleWorkTypeChange = (opt: any) => {
    setSelectedWorkType(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, workTypeId: val }));
    persistStorage({ manual_workTypeId: val });
  };

  useEffect(() => {
    if (!isStorageLoaded || !activeEmpId) return;

    const todayDate = moment().format('YYYY-MM-DD');

    fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${todayDate}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const targetData = data?.data || data;
        const periodObj = targetData?.AccountEmployeeTimeEntryPeriod || {};
        if (periodObj?.AccountEmployeeTimeEntryPeriodId) {
          setAccountEmployeeTimeEntryPeriodId(periodObj.AccountEmployeeTimeEntryPeriodId);
          setPeriodDates({
            startDate: periodObj?.TimeEntryStartDate || todayDate,
            endDate: periodObj?.TimeEntryEndDate || todayDate,
            viewType: periodObj?.TimeEntryViewType || 'Weekly'
          });
        }
      })
      .catch((err) => console.error('Error fetching period:', err));

    dispatch(getTimer() as any).then((action: any) => {
      const payload = action?.payload;
      const timerList = Array.isArray(payload) ? payload : (payload?.data || []);
      
      if (timerList.length > 0) {
        const activeTimer = timerList[0];
        setLastEntryTimerId(activeTimer.TimeEntryTimerId);
        setSeconds(activeTimer.TotalSeconds || 0);
        setDescription(activeTimer.Description || '');
        setTimerState('running');

        setActiveTimerMeta({
          clientId: activeTimer.AccountClientId || null,
          projectId: activeTimer.AccountProjectId || null,
          taskId: activeTimer.AccountProjectTaskId || null,
          workTypeId: activeTimer.AccountWorkTypeId || null,
          costCenterId: activeTimer.AccountCostCenterId || null
        });
      }
    });
  }, [isStorageLoaded, activeEmpId, dispatch]);


  useEffect(() => {
    if (!isStorageLoaded || !activeEmpId) return;

    if (isClientVisible) {
      setLoadingClients(true);
      fetchAPI(`api/Timesheets/GetAccountClientForTimesheet/${activeEmpId}/0`, 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const formatted = list.map((item: any) => ({
            value: item.AccountClientId,
            label: item.ClientName,
          }));
          setClientOptions(formatted);

          const targetId = activeTimerMeta.clientId || savedIds.clientId;
          if (targetId) {
            const match = formatted.find((c: any) => c.value === targetId);
            if (match) setSelectedClient(match);
          }
        })
        .catch((err) => console.error('Error fetching clients:', err))
        .finally(() => setLoadingClients(false));
    } else {
      setLoadingProjects(true);
      fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/0/${activeEmpId}`, 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const formatted = list.map((item: any) => ({
            value: item.AccountProjectId,
            label: item.ProjectName,
          }));
          setProjectOptions(formatted);

          const targetId = activeTimerMeta.projectId || savedIds.projectId;
          if (targetId) {
            const match = formatted.find((p: any) => p.value === targetId);
            if (match) setSelectedProject(match);
          }
        })
        .catch((err) => console.error('Error fetching projects:', err))
        .finally(() => setLoadingProjects(false));
    }

    if (isWorkTypeVisible) {
      setLoadingWorkTypes(true);
      fetchAPI('api/WorkTypes', 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const formatted = list.map((item: any) => ({
            value: item.AccountWorkTypeId,
            label: item.AccountWorkType,
          }));
          setWorkTypeOptions(formatted);

          const targetId = activeTimerMeta.workTypeId || savedIds.workTypeId;
          if (targetId) {
            const match = formatted.find((w: any) => w.value === targetId);
            if (match) setSelectedWorkType(match);
          } else if (defaultWorkTypeId) {
            const defaultWT = formatted.find((wt: any) => wt.value === defaultWorkTypeId);
            if (defaultWT) setSelectedWorkType(defaultWT);
          }
        })
        .catch((err) => console.error('Error fetching work types:', err))
        .finally(() => setLoadingWorkTypes(false));
    }
  }, [
    isStorageLoaded, 
    activeEmpId, 
    isClientVisible, 
    isWorkTypeVisible, 
    defaultWorkTypeId, 
    savedIds.clientId, 
    savedIds.projectId, 
    savedIds.workTypeId, 
    activeTimerMeta.clientId,
    activeTimerMeta.projectId,
    activeTimerMeta.workTypeId
  ]);


  useEffect(() => {
    if (!isClientVisible || !selectedClient?.value || !activeEmpId) {
      if (isClientVisible && !selectedClient) setProjectOptions([]);
      return;
    }

    setLoadingProjects(true);
    fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/${selectedClient.value}/${activeEmpId}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const formatted = list.map((item: any) => ({
         
          value: item.AccountProjectId,
          label: item.ProjectName,
        }));
        setProjectOptions(formatted);

        const targetId = activeTimerMeta.projectId || savedIds.projectId;
        if (targetId) {
          const match = formatted.find((p: any) => p.value === targetId);
          if (match) setSelectedProject(match);
        }
      })
      .catch((err) => console.error('Error fetching projects:', err))
      .finally(() => setLoadingProjects(false));
  }, [selectedClient?.value, activeEmpId, isClientVisible, savedIds.projectId, activeTimerMeta.projectId]);

  useEffect(() => {
    if (!selectedProject?.value || !activeEmpId) {
      setTaskOptions([]);
      return;
    }

    setLoadingTasks(true);
    fetchAPI(`api/Timesheets/GetAccountProjectTasksForTimesheet/${selectedProject.value}/${activeEmpId}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const formatted = list.map((item: any) => ({
          value: item.AccountProjectTaskId,
          label: item.TaskName,
        }));
        setTaskOptions(formatted);

        const targetId = activeTimerMeta.taskId || savedIds.taskId;
        if (targetId) {
          const match = formatted.find((t: any) => t.value === targetId);
          if (match) setSelectedTask(match);
        }
      })
      .catch((err) => console.error('Error fetching tasks:', err))
      .finally(() => setLoadingTasks(false));
  }, [selectedProject?.value, activeEmpId, savedIds.taskId, activeTimerMeta.taskId]);


  useEffect(() => {
    if (!isCostCenterVisible || !selectedTask?.value || !selectedProject?.value || !activeEmpId) {
      setCostCenterOptions([]);
      return;
    }

    setLoadingCostCenters(true);
    fetchAPI(`api/v2/CostCenter/GetAccountCostCenterForTimesheet/${activeEmpId}/${selectedProject.value}/${selectedTask.value}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const formatted = list.map((item: any) => ({
          value: item.AccountCostCenterId,
          label: item.AccountCostCenter,
        }));
        setCostCenterOptions(formatted);

        const targetId = activeTimerMeta.costCenterId || savedIds.costCenterId;
        if (targetId) {
          const match = formatted.find((c: any) => c.value === targetId);
          if (match) setSelectedCostCenter(match);
        } else if (formatted.length > 0) {
          setSelectedCostCenter(formatted[0]);
        }
      })
      .catch((err) => console.error('Error fetching cost centers:', err))
      .finally(() => setLoadingCostCenters(false));
  }, [selectedTask?.value, selectedProject?.value, activeEmpId, isCostCenterVisible, savedIds.costCenterId, activeTimerMeta.costCenterId]);

  useEffect(() => {
    let interval: any = null;
    if (timerState === 'running') {
      interval = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timerState]);

  const formatTime = (totalSeconds: number) => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartTimer = async () => {
    const todayDate = moment().format('YYYY-MM-DD');
    const object = {
      AccountEmployeeTimeEntryPeriodId: accountEmployeeTimeEntryPeriodId || null,
      AccountClientId: isClientVisible ? (selectedClient?.value || 0) : 0,
      AccountProjectId: selectedProject?.value || 0,
      AccountProjectTaskId: selectedTask?.value || 0,
      TimerDate: todayDate,
      TimeEntryStartDate: periodDates.startDate || todayDate,
      TimeEntryEndDate: periodDates.endDate || todayDate,
      TimeEntryViewType: periodDates.viewType || "Weekly",
      AccountEmployeeTimeEntryId: null,
      AccountEmployeeId: Number(activeEmpId),
      AccountCostCenterId: isCostCenterVisible ? (selectedCostCenter?.value || 0) : 0,
      AccountWorkTypeId: isWorkTypeVisible ? (selectedWorkType?.value || defaultWorkTypeId || 0) : (defaultWorkTypeId || 0),
      Description: description,
      TimeZoneId: timeZoneId,
      SystemPlatformSourceId: "50E254BE-D515-4FBA-BFB7-DFD27852D023"
    };

    try {
      const response = await fetchAPI(`api/Timesheets/Timer?IsTimeEntryAutoSave=true`, 'POST', object, true);
      if (response.ok) {
        const json = await response.json();
        const newTimerId = json.id || json.TimeEntryTimerId;
        if (Number(newTimerId) > 0) {
          setLastEntryTimerId(newTimerId);
          setTimerState('running');
        }
      }
    } catch (err) {
      console.error('Error starting timer API:', err);
    }
  };

  const handleStopTimer = () => {
    if (seconds < 60) {
      alert("To save this entry total time must be 1 minute or above.");
      return;
    }
    setTimerState('stopped');
  };

  const handleSaveEntry = async () => {
    try {
      const stopPayload = {
        TimeEntryTimerStatus: 5,
        AccountEmployeeTimeEntryPeriodId: accountEmployeeTimeEntryPeriodId || null,
        Description: description,
        SystemPlatformSourceId: "50E254BE-D515-4FBA-BFB7-DFD27852D023"
      };

      const response = await fetchAPI(
        `api/Timesheets/ReactTimer/${lastEntryTimerId}?IsFromReact=true&totalSeconds=${seconds}`, 
        'POST', 
        stopPayload, 
        true
      );

      if (response.ok) {
        const result = await response.json();
        if (Number(result.id) > 0 || result.success) {
          alert("Time Entry Saved Successfully!");
          setTimerState('idle');
          setSeconds(0);
          setDescription('');
          setLastEntryTimerId(null);
        } else {
          alert("Failed to save entry.");
        }
      }
    } catch (err) {
      console.error('Error saving timer entry:', err);
    }
  };

  if (timerState === 'stopped') {
    return (
      <div className="d-flex flex-column h-100 justify-content-between">
        <div>
          <div className="text-center mb-3">
            <div className="symbol symbol-35px symbol-circle bg-light-success mb-2">
              <i className="bi bi-check2 text-success fs-3"></i>
            </div>
            <h6 className="fw-bolder text-gray-900 m-0 fs-7">Time Entry Complete</h6>
          </div>

          <div className="d-flex justify-content-between align-items-center bg-light-primary p-2 px-3 rounded mb-3">
            <span className="fs-8 fw-bold text-gray-700">Duration</span>
            <span className="fs-7 fw-bolder text-primary font-monospace">{formatTime(seconds)}</span>
          </div>

          <div className="mb-3">
            {isClientVisible && (
              <div className="fs-8 mb-1 d-flex">
                <span className="text-gray-500 w-80px">Client:</span>
                <span className="fw-bold text-gray-800 text-truncate">{selectedClient?.label || 'N/A'}</span>
              </div>
            )}
            <div className="fs-8 mb-1 d-flex">
              <span className="text-gray-500 w-80px">Project:</span>
              <span className="fw-bold text-gray-800 text-truncate">{selectedProject?.label || 'N/A'}</span>
            </div>
            <div className="fs-8 mb-1 d-flex">
              <span className="text-gray-500 w-80px">Task:</span>
              <span className="fw-bold text-gray-800 text-truncate">{selectedTask?.label || 'N/A'}</span>
            </div>
            {isCostCenterVisible && (
              <div className="fs-8 mb-1 d-flex">
                <span className="text-gray-500 w-80px">Cost Center:</span>
                <span className="fw-bold text-gray-800 text-truncate">{selectedCostCenter?.label || 'N/A'}</span>
              </div>
            )}
            {isWorkTypeVisible && (
              <div className="fs-8 mb-1 d-flex">
                <span className="text-gray-500 w-80px">Work Type:</span>
                <span className="fw-bold text-gray-800 text-truncate">{selectedWorkType?.label || 'N/A'}</span>
              </div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label fs-8 fw-semibold text-gray-700 mb-1">What did you work on?</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-control form-control-sm fs-8 w-100"
              rows={3}
              style={{ resize: 'none' }}
              placeholder="Add a short description..."
            />
          </div>
        </div>

        <div className="pt-2">
          <button 
            type="button"
            onClick={handleSaveEntry} 
            className="btn btn-extension-primary btn-sm w-100 mb-2 fs-7 fw-bold py-2"
          >
            Save Entry
          </button>
          <button
            type="button" 
            onClick={() => setTimerState('running')} 
            className="btn btn-active-light btn-color-gray-600 btn-sm w-100 fs-8 fw-semibold"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100 justify-content-between">
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bolder text-gray-900 fs-7 m-0">Select what you're working on</h6>
          {(selectedClient || selectedProject || selectedTask || selectedCostCenter || selectedWorkType || description) && timerState === 'idle' && (
            <button
              type="button"
              onClick={handleClearAll}
              className="btn btn-icon btn-active-light-danger btn-sm p-1 border-0"
              title="Clear all dropdowns and fields"
              style={{ width: '28px', height: '28px' }}
            >
              <i className="bi bi-arrow-counterclockwise fs-7 text-danger"></i>
            </button>
          )}
        </div>

        {(timerState === 'running' || timerState === 'paused') && (
          <div className="alert alert-custom alert-light-success border-0 fade show p-2 mb-3 text-center rounded-pill">
            <span className="badge badge-circle badge-success me-2"></span>
            <span className="fs-8 fw-bold text-success">
              {timerState === 'running' ? 'Timer in progress' : 'Timer paused'}
            </span>
          </div>
        )}

        {(timerState === 'running' || timerState === 'paused') && (
          <div className="text-center my-3">
            <div className="display-5 fw-bolder text-success font-monospace">{formatTime(seconds)}</div>
            <span className="fs-8 text-gray-400 fw-semibold">
              {timerState === 'running' ? 'Time in progress' : 'Paused at this duration'}
            </span>
          </div>
        )}

        {isClientVisible && (
          <div className="mb-3">
            <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Client</label>
            <Select
              value={selectedClient}
              onChange={handleClientChange}
              options={clientOptions}
              isLoading={loadingClients}
              isDisabled={timerState === 'running' || timerState === 'paused'}
              styles={dropDownSelectionStyles}
              placeholder="Select Client"
            />
          </div>
        )}

        <div className="mb-3">
          <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Project</label>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            options={projectOptions}
            isLoading={loadingProjects}
            isDisabled={(isClientVisible && !selectedClient) || timerState === 'running' || timerState === 'paused'}
            styles={dropDownSelectionStyles}
            placeholder="Select Project"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Task</label>
          <Select
            value={selectedTask}
            onChange={handleTaskChange}
            options={taskOptions}
            isLoading={loadingTasks}
            isDisabled={!selectedProject || timerState === 'running' || timerState === 'paused'}
            styles={dropDownSelectionStyles}
            placeholder="Select Task"
          />
        </div>

        {isCostCenterVisible && (
          <div className="mb-3">
            <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Cost Center</label>
            <Select
              value={selectedCostCenter}
              onChange={handleCostCenterChange}
              options={costCenterOptions}
              isLoading={loadingCostCenters}
              isDisabled={!selectedTask || timerState === 'running' || timerState === 'paused'}
              styles={dropDownSelectionStyles}
              placeholder="Select Cost Center"
            />
          </div>
        )}

        {isWorkTypeVisible && (
          <div className="mb-3">
            <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Work Type</label>
            <Select
              value={selectedWorkType}
              onChange={handleWorkTypeChange}
              options={workTypeOptions}
              isLoading={loadingWorkTypes}
              isDisabled={timerState === 'running' || timerState === 'paused'}
              styles={dropDownSelectionStyles}
              placeholder="Select Work Type"
            />
          </div>
        )}
      </div>

      {timerState === 'idle' && (
        <div className="mt-4">
          <button
            type="button"
            disabled={!selectedTask}
            onClick={handleStartTimer}
            className="btn btn-extension-primary btn-sm w-100 mb-3 fs-7 fw-bold py-2 shadow-sm"
          >
            <i className="bi bi-play-fill fs-5 me-1"></i> Start Timer
          </button>
          <div className="separator separator-content my-3 text-gray-400 fs-9">or</div>
          <button
            type="button"
            onClick={onSwitchToManual}
            className="btn btn-extension-light-primary btn-sm w-100 fs-7 fw-bold py-2"
          >
            <i className="bi bi-calendar-plus fs-7 me-1"></i> Enter Time Manually
          </button>
        </div>
      )}

      {(timerState === 'running' || timerState === 'paused') && (
        <div className="mt-4 d-flex gap-2">
          <button
            type="button"
            onClick={() => setTimerState(timerState === 'running' ? 'paused' : 'running')}
            className="btn btn-outline btn-outline-primary btn-active-light-primary btn-sm flex-fill fs-7 fw-bold py-2"
          >
            <i className={`bi ${timerState === 'running' ? 'bi-pause-fill' : 'bi-play-fill'} me-1`}></i>
            {timerState === 'running' ? 'Pause' : 'Resume'}
          </button>
          <button
            type="button"
            onClick={handleStopTimer}
            className="btn btn-outline btn-outline-danger btn-active-light-danger btn-sm flex-fill fs-7 fw-bold py-2"
          >
            <i className="bi bi-square-fill fs-9 me-1"></i> Stop
          </button>
        </div>
      )}
    </div>
  );
};

export default ExtensionTimer;
